import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Plus, ShieldAlert, RefreshCw, Search, Rocket, AlertTriangle,
} from "lucide-react";

// ---------------------------------------------------------------------------
// MP12 Module 5A — Release Intelligence Desk (list).
// Internal-only. Filters: pathway, readiness, status, owner, blocked/unblocked.
// ---------------------------------------------------------------------------

const STATUS_STYLE = {
  PLANNING:  { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  IN_MOTION: { bg: "hsl(210 45% 94%)", fg: "hsl(210 55% 30%)", border: "hsl(210 45% 74%)" },
  BLOCKED:   { bg: "hsl(0 55% 94%)",   fg: "hsl(0 65% 30%)",   border: "hsl(0 55% 72%)" },
  DELAYED:   { bg: "hsl(40 55% 94%)",  fg: "hsl(35 60% 28%)",  border: "hsl(40 55% 76%)" },
  COMPLETED: { bg: "hsl(150 45% 92%)", fg: "hsl(150 45% 24%)", border: "hsl(150 40% 66%)" },
  CANCELLED: { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  ARCHIVED:  { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
};

export function ReleaseStatusBadge({ value, testid }) {
  const st = STATUS_STYLE[value] || STATUS_STYLE.PLANNING;
  return (
    <Badge
      variant="outline"
      className="text-[10px] uppercase tracking-[0.14em] font-semibold"
      style={{ background: st.bg, color: st.fg, borderColor: st.border }}
      data-testid={testid || "release-status-badge"}
    >
      {value || "—"}
    </Badge>
  );
}

function ReadinessBadge({ value }) {
  const map = {
    NOT_READY:     { fg: "hsl(0 65% 30%)",   bg: "hsl(0 55% 94%)",   bd: "hsl(0 55% 72%)" },
    PARTIAL:       { fg: "hsl(35 60% 28%)",  bg: "hsl(40 55% 94%)",  bd: "hsl(40 55% 76%)" },
    READY_INTERNAL:{ fg: "hsl(210 55% 30%)", bg: "hsl(210 45% 94%)", bd: "hsl(210 45% 74%)" },
    READY_CLIENT:  { fg: "hsl(150 45% 24%)", bg: "hsl(150 45% 92%)", bd: "hsl(150 40% 66%)" },
    RELEASED:      { fg: "hsl(150 45% 24%)", bg: "hsl(150 45% 92%)", bd: "hsl(150 40% 66%)" },
    N_A:           { fg: "hsl(220 6% 40%)",  bg: "hsl(220 8% 94%)",  bd: "hsl(220 6% 78%)" },
  };
  const st = map[value] || map.N_A;
  return (
    <Badge
      variant="outline"
      className="text-[10px] uppercase tracking-[0.14em] font-semibold"
      style={{ background: st.bg, color: st.fg, borderColor: st.bd }}
      data-testid="release-readiness-badge"
    >
      {value || "—"}
    </Badge>
  );
}

function CreateReleaseDialog({ vocab, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", release_objective: "", territory: "", target_date: "" });

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/release-intel", { ...form, title: form.title.trim() });
      toast.success("Release intelligence record created.");
      onCreated?.(r.data);
      setOpen(false);
      setForm({ title: "", release_objective: "", territory: "", target_date: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="ri-new" className="gap-2">
          <Plus className="w-4 h-4" /> New release intel
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="ri-new-dialog">
        <DialogHeader>
          <DialogTitle>Create Release Intelligence Record</DialogTitle>
          <DialogDescription>Internal-only. Links to canonical rights, compliance, and pathway data — never duplicates them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="ri-title">Title <span className="text-destructive">*</span></Label>
            <Input id="ri-title" data-testid="ri-title" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} />
          </div>
          <div>
            <Label htmlFor="ri-objective">Release objective</Label>
            <Textarea id="ri-objective" data-testid="ri-objective" rows={3} value={form.release_objective} onChange={(e)=>setForm({...form, release_objective: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ri-territory">Territory</Label>
              <Input id="ri-territory" data-testid="ri-territory" value={form.territory} onChange={(e)=>setForm({...form, territory: e.target.value})} placeholder="US, EU, worldwide…" />
            </div>
            <div>
              <Label htmlFor="ri-target">Target date / window</Label>
              <Input id="ri-target" data-testid="ri-target" value={form.target_date} onChange={(e)=>setForm({...form, target_date: e.target.value})} placeholder="2026-06-01" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)} data-testid="ri-new-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ri-new-save">{saving ? "Creating…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ReleaseIntelDesk() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [readiness, setReadiness] = useState("");
  const [blocked, setBlocked] = useState("");
  const [vocab, setVocab] = useState({ statuses: [], readiness_states: [], blocked_pathways: [] });
  const mayWrite = canWrite(user);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      if (readiness) params.readiness = readiness;
      if (blocked) params.blocked = blocked;
      const r = await api.get("/release-intel", { params });
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }, [q, status, readiness, blocked]);

  useEffect(() => {
    api.get("/release-intel/meta/vocab").then((r)=>setVocab(r.data || {})).catch(()=>{});
    load();
  }, [load]);

  if (!mayWrite) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader><CardTitle>Release Intelligence</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">This workspace is available only to Super Admin, GFMP Reviewer, and Catalog Manager.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="release-intel-desk">
      <div className="flex flex-wrap items-center gap-3">
        <Rocket className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Release Intelligence</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Internal operational decision-support for release &amp; campaign planning. Links to canonical rights, compliance, LOC-prep, artwork review, communications, and sync activity — never duplicates them.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]" data-testid="ri-internal-badge">Internal-only</Badge>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="ri-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
        <CreateReleaseDialog vocab={vocab} onCreated={(r) => navigate(`/advanced/release-intel/${r.id}`)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Search className="w-4 h-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label>Search</Label>
              <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Title, reference, territory…" data-testid="ri-filter-q" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={status || "__any__"} onValueChange={(v)=>setStatus(v === "__any__" ? "" : v)}>
                <SelectTrigger data-testid="ri-filter-status"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any status</SelectItem>
                  {(vocab.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Readiness</Label>
              <Select value={readiness || "__any__"} onValueChange={(v)=>setReadiness(v === "__any__" ? "" : v)}>
                <SelectTrigger data-testid="ri-filter-readiness"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any readiness</SelectItem>
                  {(vocab.readiness_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Blocked?</Label>
              <Select value={blocked || "__any__"} onValueChange={(v)=>setBlocked(v === "__any__" ? "" : v)}>
                <SelectTrigger data-testid="ri-filter-blocked"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any</SelectItem>
                  <SelectItem value="yes">Blocked pathway present</SelectItem>
                  <SelectItem value="no">No blocked pathways</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={load} className="w-full" data-testid="ri-filter-apply">Apply</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Records ({rows.length})</CardTitle>
          <CardDescription>Click a row to open the linked-status editor.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2" data-testid="ri-empty">
              <Sparkles className="w-4 h-4" /> No release intelligence records match your filters.
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <Link
                  key={r.id}
                  to={`/advanced/release-intel/${r.id}`}
                  className="block border border-border rounded-md px-4 py-3 hover:bg-accent transition-colors"
                  data-testid={`ri-row-${r.id}`}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-xs opacity-70">{r.internal_reference || "—"}</span>
                    <span className="font-semibold text-sm">{r.title || "(untitled)"}</span>
                    <ReleaseStatusBadge value={r.status} testid={`ri-row-status-${r.id}`} />
                    <ReadinessBadge value={r.readiness_state} />
                    {r.territory && <span className="text-xs opacity-70">· {r.territory}</span>}
                    {r.target_date && <span className="text-xs opacity-70">· target: {r.target_date}</span>}
                    {(r.blockers || []).filter((b)=>!b.resolved_at).length > 0 && (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ background: "hsl(0 55% 94%)", color: "hsl(0 65% 30%)", borderColor: "hsl(0 55% 72%)" }}>
                        <AlertTriangle className="w-3 h-3 mr-1" /> {(r.blockers || []).filter((b)=>!b.resolved_at).length} blocker
                      </Badge>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground flex items-center gap-2">
        <ShieldAlert className="w-3.5 h-3.5" />
        Blocked pathway assets (DEMO_ONLY, DEVELOPMENT_CANDIDATE, CLEARANCE_REVIEW) cannot be marked release-ready. Server enforces this even if the UI is bypassed.
      </p>
    </div>
  );
}
